import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronLeft, ChevronRight, Copy, ExternalLink, FileText } from "lucide-react";
import type { PageEntry, SiteData } from "../shared/types";
import { markdownUrl, siteUrl, LLMS_TXT } from "../shared/paths";
import { agentPreface } from "../shared/agents";
import { COPY, fill } from "../copy";
import { InlineText } from "../components/InlineText";
import { siteOrigin } from "./origin";

/**
 * The top and bottom of every page (design §0.2): breadcrumb, title, subtitle and the "Copy page"
 * menu (Copy page · View as Markdown · Open in ChatGPT / Claude); at the end "Was this page
 * helpful?", previous / next cards and the footer. The invisible agent note points at the page's
 * Markdown twin and `llms.txt` (§0.9).
 */

const absolute = (path: string) => `${siteOrigin()}${path}`;

export const CopyPageMenu = ({ path }: { path: string }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const mdUrl = markdownUrl(path);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);

    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const copyPage = () =>
    fetch(mdUrl)
      .then((res) => res.text())
      .then((text) => navigator.clipboard?.writeText(text))
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      });
  const prompt = encodeURIComponent(fill(COPY.askAboutPage, { url: absolute(mdUrl) }));

  return (
    <div ref={ref} className="relative inline-flex" data-pagefind-ignore>
      <button type="button" onClick={copyPage} className="flex items-center gap-1.5 rounded-l-lg border border-border px-3 py-1.5 text-bodySmall text-foreground hover:bg-muted">
        <Copy size={14} />
        {copied ? COPY.copyPageDone : COPY.copyPage}
      </button>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded-r-lg border border-l-0 border-border px-2 text-muted-foreground hover:bg-muted"
        aria-expanded={open}
        aria-label={COPY.copyPage}
      >
        <ChevronDown size={14} />
      </button>
      {open ? (
        <ul className="absolute right-0 top-full z-20 mt-1 w-56 rounded-lg border border-border bg-popover p-1 text-bodySmall shadow-lg">
          <li>
            <a href={mdUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-muted">
              <FileText size={14} />
              {COPY.viewAsMarkdown}
            </a>
          </li>
          <li>
            <a href={`https://chatgpt.com/?q=${prompt}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-muted">
              <ExternalLink size={14} />
              {COPY.openInChatGpt}
            </a>
          </li>
          <li>
            <a href={`https://claude.ai/new?q=${prompt}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-muted">
              <ExternalLink size={14} />
              {COPY.openInClaude}
            </a>
          </li>
        </ul>
      ) : null}
    </div>
  );
};

export const PageHeader = ({ entry }: { entry: PageEntry }) => (
  <div className="mb-8">
    <p className="sr-only" data-pagefind-ignore>
      {agentPreface(absolute(LLMS_TXT), absolute(markdownUrl(entry.path)))}
    </p>
    {entry.breadcrumb.length ? (
      <p data-pagefind-ignore className="mb-2 text-bodySmall text-purpleGreen">
        {entry.breadcrumb.join(" › ")}
      </p>
    ) : null}
    <div className="flex flex-wrap items-start justify-between gap-3">
      <h1 className="text-head2 font-semibold text-foreground">{entry.title}</h1>
      <CopyPageMenu path={entry.path} />
    </div>
    {entry.subtitle ? (
      <p className="mt-2 text-bodyLarge text-muted-foreground">
        <InlineText text={entry.subtitle} />
      </p>
    ) : null}
  </div>
);

const Helpful = () => {
  const [answered, setAnswered] = useState(false);

  return (
    <div className="flex items-center gap-3 text-bodySmall text-muted-foreground">
      {answered ? (
        <span>{COPY.thanks}</span>
      ) : (
        <>
          <span>{COPY.helpful}</span>
          <button type="button" onClick={() => setAnswered(true)} className="rounded-full border border-border px-3 py-1 hover:text-foreground">
            {COPY.yes}
          </button>
          <button type="button" onClick={() => setAnswered(true)} className="rounded-full border border-border px-3 py-1 hover:text-foreground">
            {COPY.no}
          </button>
        </>
      )}
    </div>
  );
};

export const PageFooter = ({ entry, site }: { entry: PageEntry; site: SiteData }) => (
  <footer className="mt-16 border-t border-border pt-6" data-pagefind-ignore>
    <Helpful />
    <div className="mt-6 grid grid-cols-1 gap-4 clg:grid-cols-2">
      {entry.prev ? (
        <Link to={siteUrl(entry.prev.path)} className="rounded-lg border border-border p-4 hover:border-foreground/40">
          <span className="flex items-center gap-1 text-bodySmall text-muted-foreground">
            <ChevronLeft size={14} />
            {COPY.previous}
          </span>
          <span className="mt-1 block font-semibold text-foreground">{entry.prev.title}</span>
        </Link>
      ) : (
        <span />
      )}
      {entry.next ? (
        <Link to={siteUrl(entry.next.path)} className="rounded-lg border border-border p-4 text-right hover:border-foreground/40">
          <span className="flex items-center justify-end gap-1 text-bodySmall text-muted-foreground">
            {COPY.next}
            <ChevronRight size={14} />
          </span>
          <span className="mt-1 block font-semibold text-foreground">{entry.next.title}</span>
          {entry.next.subtitle ? <span className="mt-1 block text-bodySmall text-muted-foreground">{entry.next.subtitle}</span> : null}
        </Link>
      ) : null}
    </div>
    <div className="mt-10 flex flex-wrap gap-4 text-bodySmall text-muted-foreground">
      {site.links.status ? <a href={site.links.status}>{COPY.status}</a> : null}
      <a href={site.links.terms}>{COPY.terms}</a>
      <a href={site.links.privacy}>{COPY.privacy}</a>
    </div>
  </footer>
);
